import React from 'react';
import styled from 'styled-components';

interface TestimonialCardProps {
    quote: string;
    authorName: string;
    authorImage: string;
    imagePosition: 'left' | 'right';
}

const CardContainer = styled.div<{ $imagePosition: 'left' | 'right' }>`
    display: grid;
    grid-template-columns: ${({ $imagePosition }) => 
        $imagePosition === 'left' ? '140px 1fr' : '1fr 140px'};
    gap: ${({ theme }) => theme.spacing.lg};
    align-items: center;
    padding: ${({ theme }) => theme.spacing.xl};
    max-width: 600px;
    margin: 0 auto;
    border: 1px solid ${({ theme }) => theme.colors.divider};
    border-radius: ${({ theme }) => theme.borderRadius.large};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: 1fr;
        gap: ${({ theme }) => theme.spacing.md};
        max-width: 100%;
    }
`;

const ImageWrapper = styled.div<{ $position: 'left' | 'right' }>`
    order: ${({ $position }) => ($position === 'left' ? 1 : 2)};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        order: 1;
        display: flex;
        justify-content: center;
    }
`;

const AuthorImage = styled.img`
    width: 140px;
    height: 140px;
    object-fit: cover;
    border-radius: 50%;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 120px;
        height: 120px;
    }
`;

const ContentWrapper = styled.div<{ $position: 'left' | 'right' }>`
    order: ${({ $position }) => ($position === 'left' ? 2 : 1)};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        order: 2;
    }
`;

const Quote = styled.blockquote`
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    font-style: italic;
    line-height: 1.6;
    margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
    color: ${({ theme }) => theme.colors.text};
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.divider};
    margin: ${({ theme }) => theme.spacing.md} 0;
`;

const AuthorName = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.text};
`;

export const TestimonialCard: React.FC<TestimonialCardProps> = ({ 
    quote, 
    authorName, 
    authorImage,
    imagePosition
}) => {
    return (
        <CardContainer $imagePosition={imagePosition}>
            <ImageWrapper $position={imagePosition}>
                <AuthorImage src={authorImage} alt={authorName} />
            </ImageWrapper>
            <ContentWrapper $position={imagePosition}>
                <Quote>"{quote}"</Quote>
                <Divider />
                <AuthorName>{authorName}</AuthorName>
            </ContentWrapper>
        </CardContainer>
    );
};
